import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';
import {
  getBookingPolicies,
  getBookingPolicyById,
  createBookingPolicy,
  updateBookingPolicy,
  deleteBookingPolicy,
  toggleBookingPolicyStatus,
  getFormattedPolicyText,
} from '../../Services/Operations/ProductAPI';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Policy Name</th>
          <th>Cancellation Hours</th>
          <th>Child Age Limit</th>
          <th>Refund %</th>
          <th>Applicable For</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal">
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        {[1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div key={i} className="skeleton-detail-row">
            <div className="skeleton-label"></div>
            <div className="skeleton-value"></div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const BookingPolicyManagement = () => {
  const { token } = useSelector(state => state.user);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newPolicyForm, setNewPolicyForm] = useState({
    policyName: '',
    cancellationHours: 4,
    childAgeLimit: 5,
    requiredIdProof: true,
    refundPercentage: 100,
    applicableFor: ['all'],
    description: '',
    termsAndConditions: '',
    isActive: true,
    policyType: 'standard'
  });
  const [error, setError] = useState(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [formattedText, setFormattedText] = useState(null);
  const [showFormattedText, setShowFormattedText] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPolicies, setTotalPolicies] = useState(0);
  const [pageSize] = useState(10);

  const getSequentialNumber = (index) => {
    return (currentPage - 1) * pageSize + index + 1;
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPolicies();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm]);

  const extractHoursFromText = (text) => {
    if (!text) return 4;
    const match = text.match(/(\d+)\s*hours?/i);
    return match ? parseInt(match[1]) : 4;
  };

  const extractAgeFromText = (text) => {
    if (!text) return 5;
    const match = text.match(/above\s*(\d+)\s*years?/i);
    return match ? parseInt(match[1]) : 5;
  };

  const extractRefundFromText = (text) => {
    if (!text) return 100;
    const match = text.match(/(\d+)%/);
    return match ? parseInt(match[1]) : 100;
  };

  const fetchPolicies = async () => {
    try {
      setSearching(true);
      setLoading(true);
      const response = await getBookingPolicies(token, currentPage, pageSize, searchTerm);

      if (response && response.data) {
        const transformedPolicies = response.data.map((policy, index) => {
          const isInstant = policy.policyName && policy.policyName.toLowerCase().includes('instant');
          
          return {
            id: policy.id || policy._id,
            serialNo: getSequentialNumber(index),
            policyName: policy.policyName || 'Untitled Policy',
            cancellationHours: policy.cancellationHours !== undefined && policy.cancellationHours !== null 
              ? policy.cancellationHours 
              : extractHoursFromText(policy.cancellationPolicy) || 4,
            childAgeLimit: policy.childAgeLimit !== undefined && policy.childAgeLimit !== null
              ? policy.childAgeLimit
              : extractAgeFromText(policy.childTicketPolicy) || 5,
            refundPercentage: policy.refundPercentage !== undefined && policy.refundPercentage !== null
              ? policy.refundPercentage
              : extractRefundFromText(policy.refundPolicy) || 100,
            applicableFor: policy.applicableFor || ['all'],
            isActive: policy.isActive !== undefined ? policy.isActive : true,
            status: policy.isActive ? 'Active' : 'Inactive',
            description: policy.description || '',
            termsAndConditions: policy.termsAndConditions || '',
            requiredIdProof: policy.idProofRequirement || 'Valid ID proof required',
            createdAt: policy.createdAt || new Date().toISOString(),
            updatedAt: policy.updatedAt || new Date().toISOString(),
            cancellationPolicy: policy.cancellationPolicy || `Cancellation allowed up to ${policy.cancellationHours || 4} hours before departure`,
            refundPolicy: policy.refundPolicy || `${policy.refundPercentage || 100}% refund applicable on valid cancellations`,
            childTicketPolicy: policy.childTicketPolicy || `Children above ${policy.childAgeLimit || 5} years need a full ticket`,
            idProofRequirementText: policy.idProofRequirement || 'Valid ID proof required for all passengers',
            isInstant: isInstant
          };
        });

        setPolicies(transformedPolicies);
        setTotalPages(response.totalPages || 1);
        setTotalPolicies(response.total || response.data.length);
      }
    } catch (err) {
      setError('Failed to fetch booking policies.');
      console.error('Error fetching policies:', err);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleViewPolicy = async (policyId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      
      const response = await getBookingPolicyById(policyId, token);
      
      if (response.success) {
        const policy = response.data;
        const isInstant = policy.policyName && policy.policyName.toLowerCase().includes('instant');
        
        setViewingPolicy({
          id: policy.id || policy._id,
          policyName: policy.policyName || 'Untitled Policy',
          cancellationPolicy: isInstant 
            ? 'Instant Ticket Can Not Be Cancel' 
            : (policy.cancellationPolicy || `Cancellation allowed up to ${policy.cancellationHours || 4} hours before departure`),
          childTicketPolicy: policy.childTicketPolicy || `Children above ${policy.childAgeLimit || 5} years need a full ticket`,
          idProofRequirement: policy.idProofRequirement || 'Valid ID proof required for all passengers',
          refundPercentage: policy.refundPercentage !== undefined && policy.refundPercentage !== null
            ? policy.refundPercentage
            : extractRefundFromText(policy.refundPolicy) || 100,
          applicableFor: policy.applicableFor || ['all'],
          description: isInstant ? 'Instant Ticket Can Not Be Cancel' : (policy.description || ''),
          termsAndConditions: isInstant ? 'Instant Ticket Can Not Be Cancel' : (policy.termsAndConditions || ''),
          isActive: policy.isActive !== undefined ? policy.isActive : true,
          isInstant: isInstant,
          createdAt: policy.createdAt ? new Date(policy.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'N/A',
          updatedAt: policy.updatedAt ? new Date(policy.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }) : 'N/A'
        });
      } else {
        setError('Failed to fetch policy details.');
      }
    } catch (err) {
      console.error('Error fetching policy details:', err);
      setError('Failed to fetch policy details.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleViewFormattedText = async (policyId) => {
    try {
      setIsFetchingDetails(true);
      const response = await getFormattedPolicyText(policyId, token);
      if (response.success) {
        setFormattedText(response.data.formattedText);
        setShowFormattedText(true);
      }
    } catch (err) {
      console.error('Error fetching formatted text:', err);
      setError('Failed to fetch formatted policy text.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditPolicy = async (policyId) => {
    try {
      setIsFetchingDetails(true);
      setError(null);
      
      const response = await getBookingPolicyById(policyId, token);
      
      if (response.success) {
        const policy = response.data;
        const isInstant = policy.policyName && policy.policyName.toLowerCase().includes('instant');
        
        setEditingPolicy(policy.id || policy._id);
        setEditForm({
          policyName: policy.policyName || '',
          cancellationHours: isInstant ? 0 : (policy.cancellationHours !== undefined && policy.cancellationHours !== null
            ? policy.cancellationHours
            : extractHoursFromText(policy.cancellationPolicy) || 4),
          childAgeLimit: policy.childAgeLimit !== undefined && policy.childAgeLimit !== null
            ? policy.childAgeLimit
            : extractAgeFromText(policy.childTicketPolicy) || 5,
          requiredIdProof: policy.idProofRequirement ? policy.idProofRequirement.includes('required') : true,
          refundPercentage: isInstant ? 0 : (policy.refundPercentage !== undefined && policy.refundPercentage !== null
            ? policy.refundPercentage
            : extractRefundFromText(policy.refundPolicy) || 100),
          applicableFor: policy.applicableFor || ['all'],
          description: isInstant ? 'Instant Ticket Can Not Be Cancel' : (policy.description || ''),
          termsAndConditions: isInstant ? 'Instant Ticket Can Not Be Cancel' : (policy.termsAndConditions || ''),
          isActive: policy.isActive !== undefined ? policy.isActive : true,
          isInstant: isInstant
        });
      } else {
        setError('Failed to fetch policy details for editing.');
      }
    } catch (err) {
      console.error('Error fetching policy details for editing:', err);
      setError('Failed to fetch policy details for editing.');
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);
      
      const isInstant = editForm.policyName && editForm.policyName.toLowerCase().includes('instant');
      
      const updateData = {
        policyName: editForm.policyName,
        cancellationHours: isInstant ? 0 : parseInt(editForm.cancellationHours) || 0,
        childAgeLimit: parseInt(editForm.childAgeLimit) || 5,
        requiredIdProof: editForm.requiredIdProof,
        refundPercentage: isInstant ? 0 : parseInt(editForm.refundPercentage) || 0,
        applicableFor: editForm.applicableFor,
        description: isInstant ? 'Instant Ticket Can Not Be Cancel' : editForm.description,
        termsAndConditions: isInstant ? 'Instant Ticket Can Not Be Cancel' : editForm.termsAndConditions,
        isActive: editForm.isActive
      };

      const response = await updateBookingPolicy(editingPolicy, updateData, token);
      
      if (response.success) {
        setPolicies(prevPolicies =>
          prevPolicies.map(policy =>
            policy.id === editingPolicy ? {
              ...policy,
              policyName: response.data.policyName,
              cancellationHours: response.data.cancellationHours,
              childAgeLimit: response.data.childAgeLimit,
              refundPercentage: response.data.refundPercentage,
              status: response.data.isActive ? 'Active' : 'Inactive',
              isActive: response.data.isActive,
              isInstant: isInstant
            } : policy
          )
        );
        setEditingPolicy(null);
        setEditForm({});
        alert('Booking policy updated successfully!');
        fetchPolicies();
      } else {
        setError(response.message || 'Failed to update policy.');
      }
    } catch (err) {
      console.error('Error updating policy:', err);
      setError(err.response?.data?.message || 'Failed to update policy.');
      alert('Failed to update policy. Please try again.');
    }
  };

  const handleCreatePolicy = async () => {
    try {
      setError(null);
      
      const isInstant = newPolicyForm.policyType === 'instant';
      
      const createData = {
        policyName: isInstant ? 'Instant Bus Booking Policy' : newPolicyForm.policyName,
        cancellationHours: isInstant ? 0 : parseInt(newPolicyForm.cancellationHours) || 0,
        childAgeLimit: parseInt(newPolicyForm.childAgeLimit) || 5,
        requiredIdProof: newPolicyForm.requiredIdProof,
        refundPercentage: isInstant ? 0 : parseInt(newPolicyForm.refundPercentage) || 0,
        applicableFor: newPolicyForm.applicableFor,
        description: isInstant ? 'Instant Ticket Can Not Be Cancel' : newPolicyForm.description,
        termsAndConditions: isInstant ? 'Instant Ticket Can Not Be Cancel' : newPolicyForm.termsAndConditions,
        isActive: newPolicyForm.isActive
      };

      const response = await createBookingPolicy(createData, token);
      
      if (response.success) {
        setCreatingNew(false);
        setNewPolicyForm({
          policyName: '',
          cancellationHours: 4,
          childAgeLimit: 5,
          requiredIdProof: true,
          refundPercentage: 100,
          applicableFor: ['all'],
          description: '',
          termsAndConditions: '',
          isActive: true,
          policyType: 'standard'
        });
        alert('Booking policy created successfully!');
        fetchPolicies();
      } else {
        setError(response.message || 'Failed to create policy.');
      }
    } catch (err) {
      console.error('Error creating policy:', err);
      setError(err.response?.data?.message || 'Failed to create policy.');
      alert('Failed to create policy. Please try again.');
    }
  };

  const handleToggleStatus = async (policyId, currentStatus) => {
    if (window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this policy?`)) {
      try {
        const response = await toggleBookingPolicyStatus(policyId, token);
        if (response.success) {
          alert(`Policy ${response.message}`);
          fetchPolicies();
        }
      } catch (err) {
        console.error('Error toggling policy status:', err);
        alert('Failed to update policy status.');
      }
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        const response = await deleteBookingPolicy(policyId, token);
        if (response.success) {
          alert('Policy deleted successfully!');
          fetchPolicies();
        }
      } catch (err) {
        console.error('Error deleting policy:', err);
        alert('Failed to delete policy.');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingPolicy(null);
    setEditForm({});
    setError(null);
  };

  const handleCancelCreate = () => {
    setCreatingNew(false);
    setNewPolicyForm({
      policyName: '',
      cancellationHours: 4,
      childAgeLimit: 5,
      requiredIdProof: true,
      refundPercentage: 100,
      applicableFor: ['all'],
      description: '',
      termsAndConditions: '',
      isActive: true,
      policyType: 'standard'
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNewPolicyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPolicyForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePolicyTypeChange = (e) => {
    const value = e.target.value;
    const isInstant = value === 'instant';
    
    setNewPolicyForm(prev => ({
      ...prev,
      policyType: value,
      policyName: isInstant ? 'Instant Bus Booking Policy' : prev.policyName,
      cancellationHours: isInstant ? 0 : prev.cancellationHours,
      refundPercentage: isInstant ? 0 : prev.refundPercentage,
      description: isInstant ? 'Instant Ticket Can Not Be Cancel' : prev.description,
      termsAndConditions: isInstant ? 'Instant Ticket Can Not Be Cancel' : prev.termsAndConditions
    }));
  };

  const handleApplicableForChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setNewPolicyForm(prev => ({
      ...prev,
      applicableFor: selected
    }));
  };

  const closeViewModal = () => {
    setViewingPolicy(null);
    setShowFormattedText(false);
    setFormattedText(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchPolicies();
  };

  const handleExportCSV = () => {
    if (policies.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['No.', 'Policy Name', 'Cancellation Hours', 'Child Age Limit', 'Refund %', 'Applicable For', 'Status', 'Created At'];
    const csvData = policies.map(policy => [
      policy.serialNo,
      policy.policyName,
      policy.isInstant ? 'No Cancellation' : `${policy.cancellationHours} hours`,
      `Above ${policy.childAgeLimit} years`,
      policy.isInstant ? '0%' : `${policy.refundPercentage}%`,
      policy.applicableFor.join(', '),
      policy.status,
      new Date(policy.createdAt).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `booking-policies-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    alert('CSV exported successfully!');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading && policies.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>
        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>
        <SkeletonTable />
        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Booking Policy Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search policies by name..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "70%" }}
            />
            {searching && (
              <span style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
              }}>
                searching...
              </span>
            )}
          </div>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
          <button className="add-btn" onClick={() => setCreatingNew(true)}>
            + Add Policy
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Policies</h3>
          <p>{totalPolicies}</p>
          <span>All booking policies</span>
        </div>
        <div className="stat-card">
          <h3>Active Policies</h3>
          <p>{policies.filter(p => p.status === 'Active').length}</p>
          <span>Active policies</span>
        </div>
        <div className="stat-card">
          <h3>Inactive Policies</h3>
          <p>{policies.filter(p => p.status === 'Inactive').length}</p>
          <span>Inactive policies</span>
        </div>
        <div className="stat-card">
          <h3>Search Results</h3>
          <p>{policies.length}</p>
          <span>policies found</span>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Policy Name</th>
              <th>Cancellation Hours</th>
              <th>Child Age Limit</th>
              <th>Refund %</th>
              <th>Applicable For</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {policies.length > 0 ? (
              policies.map(policy => (
                <tr key={policy.id}>
                  <td>{policy.serialNo}</td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <input
                        type="text"
                        name="policyName"
                        value={editForm.policyName || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Policy Name"
                      />
                    ) : (
                      <strong>
                        {policy.policyName}
                        {policy.isInstant && (
                          <span style={{ 
                            display: 'inline-block', 
                            marginLeft: '8px', 
                            fontSize: '10px', 
                            background: '#ff6b6b', 
                            color: 'white', 
                            padding: '2px 8px', 
                            borderRadius: '12px',
                            fontWeight: 'normal'
                          }}>
                            No Cancellation
                          </span>
                        )}
                      </strong>
                    )}
                  </td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <input
                        type="number"
                        name="cancellationHours"
                        value={editForm.cancellationHours || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Hours"
                        min="0"
                        disabled={editForm.isInstant}
                      />
                    ) : (
                      <span>
                        {policy.isInstant ? (
                          <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                            No Cancellation
                          </span>
                        ) : (
                          `${policy.cancellationHours} hours`
                        )}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <input
                        type="number"
                        name="childAgeLimit"
                        value={editForm.childAgeLimit || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="Age"
                        min="0"
                      />
                    ) : (
                      `Above ${policy.childAgeLimit} years`
                    )}
                  </td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <input
                        type="number"
                        name="refundPercentage"
                        value={editForm.refundPercentage || ''}
                        onChange={handleInputChange}
                        className="edit-input"
                        placeholder="%"
                        min="0"
                        max="100"
                        disabled={editForm.isInstant}
                      />
                    ) : (
                      <span>
                        {policy.isInstant ? (
                          <span style={{ color: '#dc3545', fontWeight: 'bold' }}>
                            0% (No Refund)
                          </span>
                        ) : (
                          `${policy.refundPercentage}%`
                        )}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <select
                        multiple
                        name="applicableFor"
                        value={editForm.applicableFor || ['all']}
                        onChange={handleInputChange}
                        className="edit-input"
                      >
                        <option value="all">All</option>
                        <option value="ac">AC</option>
                        <option value="non-ac">Non-AC</option>
                        <option value="sleeper">Sleeper</option>
                        <option value="seater">Seater</option>
                      </select>
                    ) : (
                      policy.applicableFor?.join(', ') || 'All'
                    )}
                  </td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={editForm.isActive || false}
                          onChange={handleInputChange}
                        />
                        <span className="checkbox-text">
                          {editForm.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </label>
                    ) : (
                      <span className={`status-badge ${policy.status ? policy.status.toLowerCase() : 'inactive'}`}>
                        {policy.status}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingPolicy === policy.id ? (
                      <div className="edit-actions">
                        <button className="action-btn save" onClick={handleSaveEdit} disabled={isFetchingDetails}>
                          Save
                        </button>
                        <button className="action-btn cancel" onClick={handleCancelEdit}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleViewPolicy(policy.id)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditPolicy(policy.id)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn text-view"
                          onClick={() => handleViewFormattedText(policy.id)}
                          title="View Formatted Text"
                        >
                          📄
                        </button>
                        <button
                          className={`action-btn ${policy.isActive ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleStatus(policy.id, policy.isActive)}
                          title={policy.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {policy.isActive ? '🔴' : '🟢'}
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeletePolicy(policy.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  <p>No booking policies found {searchTerm ? 'matching your search' : ''}.</p>
                  {searchTerm && (
                    <button className="clear-filters-link" onClick={handleRefresh}>
                      Clear search to see all policies
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          Showing {policies.length} of {totalPolicies} policies
          (Page {currentPage} of {totalPages})
          {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNumber}
                  className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>
          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingPolicy && !isFetchingDetails && !showFormattedText && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Policy Details - {viewingPolicy.policyName}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div className="detail-row">
                  <label>Policy Name:</label>
                  <span><strong>{viewingPolicy.policyName}</strong></span>
                </div>
                <div className="detail-row">
                  <label>Cancellation Policy:</label>
                  <span style={viewingPolicy.isInstant ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                    {viewingPolicy.cancellationPolicy}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Child Ticket Policy:</label>
                  <span>{viewingPolicy.childTicketPolicy}</span>
                </div>
                <div className="detail-row">
                  <label>ID Proof Requirement:</label>
                  <span>{viewingPolicy.idProofRequirement}</span>
                </div>
                <div className="detail-row">
                  <label>Refund Percentage:</label>
                  <span style={viewingPolicy.isInstant ? { color: '#dc3545', fontWeight: 'bold' } : {}}>
                    {viewingPolicy.isInstant ? '0% (No Refund)' : `${viewingPolicy.refundPercentage}%`}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Applicable For:</label>
                  <span>{viewingPolicy.applicableFor?.join(', ') || 'All'}</span>
                </div>
                {viewingPolicy.description && (
                  <div className="detail-row">
                    <label>Description:</label>
                    <span>{viewingPolicy.description}</span>
                  </div>
                )}
                {viewingPolicy.termsAndConditions && (
                  <div className="detail-row">
                    <label>Terms & Conditions:</label>
                    <span>{viewingPolicy.termsAndConditions}</span>
                  </div>
                )}
                <div className="detail-row">
                  <label>Created Date:</label>
                  <span>{viewingPolicy.createdAt}</span>
                </div>
                <div className="detail-row">
                  <label>Last Updated:</label>
                  <span>{viewingPolicy.updatedAt}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`status-badge ${viewingPolicy.isActive ? 'active' : 'inactive'}`}>
                    {viewingPolicy.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {viewingPolicy.isInstant && (
                  <div className="detail-row" style={{ marginTop: '10px' }}>
                    <div style={{ 
                      background: '#fff3cd', 
                      border: '1px solid #ffc107', 
                      borderRadius: '4px', 
                      padding: '10px',
                      width: '100%',
                      color: '#856404'
                    }}>
                      ⚠️ This is an Instant Booking Policy - No cancellations or refunds allowed
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {showFormattedText && formattedText && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Formatted Policy Text</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>×</button>
            </div>
            <div className="modal-body">
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                fontFamily: 'monospace', 
                fontSize: '14px',
                lineHeight: '1.6',
                background: '#f5f5f5',
                padding: '15px',
                borderRadius: '8px'
              }}>
                {formattedText}
              </pre>
            </div>
            <div className="modal-footer">
              <button 
                className="action-btn save" 
                onClick={() => {
                  navigator.clipboard.writeText(formattedText);
                  alert('Copied to clipboard!');
                }}
              >
                Copy Text
              </button>
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {creatingNew && (
        <div className="modal-overlay" onClick={handleCancelCreate}>
          <div className="modal-content1 edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Create New Booking Policy</h3>
              <button className="modal-close-btn" onClick={handleCancelCreate}>×</button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                <div className="form-group">
                  <label>Policy Type:*</label>
                  <select
                    name="policyType"
                    value={newPolicyForm.policyType || 'standard'}
                    onChange={handlePolicyTypeChange}
                    className="form-control"
                  >
                    <option value="standard">Standard Booking Policy</option>
                    <option value="instant">Instant Booking Policy (No Cancellation)</option>
                  </select>
                  <small style={{ color: newPolicyForm.policyType === 'instant' ? '#dc3545' : '#6c757d' }}>
                    {newPolicyForm.policyType === 'instant' 
                      ? '⚠️ Instant tickets cannot be cancelled and have 0% refund' 
                      : 'Standard policy with cancellation and refund options'}
                  </small>
                </div>
                
                <div className="form-group">
                  <label>Policy Name:*</label>
                  <input
                    type="text"
                    name="policyName"
                    value={newPolicyForm.policyName}
                    onChange={handleNewPolicyChange}
                    className="form-control"
                    placeholder={newPolicyForm.policyType === 'instant' ? 'Instant Bus Booking Policy' : 'E.g., Standard Booking Policy'}
                    required
                    disabled={newPolicyForm.policyType === 'instant'}
                  />
                  {newPolicyForm.policyType === 'instant' && (
                    <small style={{ color: '#6c757d' }}>
                      Policy name is automatically set for instant tickets
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Cancellation Hours (before departure):*</label>
                  <input
                    type="number"
                    name="cancellationHours"
                    value={newPolicyForm.cancellationHours}
                    onChange={handleNewPolicyChange}
                    className="form-control"
                    min="0"
                    step="1"
                    disabled={newPolicyForm.policyType === 'instant'}
                    style={newPolicyForm.policyType === 'instant' ? { background: '#e9ecef' } : {}}
                  />
                  <small>
                    {newPolicyForm.policyType === 'instant' 
                      ? 'Cancellation is not allowed for instant tickets' 
                      : 'Cancellation allowed up to X hours before departure'}
                  </small>
                </div>
                
                <div className="form-group">
                  <label>Child Age Limit:*</label>
                  <input
                    type="number"
                    name="childAgeLimit"
                    value={newPolicyForm.childAgeLimit}
                    onChange={handleNewPolicyChange}
                    className="form-control"
                    min="0"
                    step="1"
                  />
                  <small>Children above this age need full ticket</small>
                </div>
                
                <div className="form-group">
                  <label>Refund Percentage:*</label>
                  <input
                    type="number"
                    name="refundPercentage"
                    value={newPolicyForm.refundPercentage}
                    onChange={handleNewPolicyChange}
                    className="form-control"
                    min="0"
                    max="100"
                    step="1"
                    disabled={newPolicyForm.policyType === 'instant'}
                    style={newPolicyForm.policyType === 'instant' ? { background: '#e9ecef' } : {}}
                  />
                  <small>
                    {newPolicyForm.policyType === 'instant' 
                      ? 'No refund for instant tickets' 
                      : 'Percentage of refund for cancellations'}
                  </small>
                </div>
                
                <div className="form-group">
                  <label>Applicable For:</label>
                  <select
                    multiple
                    name="applicableFor"
                    value={newPolicyForm.applicableFor}
                    onChange={handleApplicableForChange}
                    className="form-control"
                    style={{ height: '100px' }}
                  >
                    <option value="all">All Bus Types</option>
                    <option value="ac">AC Buses</option>
                    <option value="non-ac">Non-AC Buses</option>
                    <option value="sleeper">Sleeper Buses</option>
                    <option value="seater">Seater Buses</option>
                  </select>
                  <small>Hold Ctrl to select multiple</small>
                </div>
                
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="requiredIdProof"
                      checked={newPolicyForm.requiredIdProof}
                      onChange={handleNewPolicyChange}
                    />
                    <span className="checkbox-text">Require Valid ID Proof</span>
                  </label>
                </div>
                
                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    name="description"
                    value={newPolicyForm.description}
                    onChange={handleNewPolicyChange}
                    className="form-control"
                    rows="3"
                    placeholder="Brief description of this policy"
                    disabled={newPolicyForm.policyType === 'instant'}
                    style={newPolicyForm.policyType === 'instant' ? { background: '#e9ecef' } : {}}
                  />
                  {newPolicyForm.policyType === 'instant' && (
                    <small style={{ color: '#6c757d' }}>
                      Description is automatically set for instant tickets
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Terms & Conditions:</label>
                  <textarea
                    name="termsAndConditions"
                    value={newPolicyForm.termsAndConditions}
                    onChange={handleNewPolicyChange}
                    className="form-control"
                    rows="4"
                    placeholder="Detailed terms and conditions..."
                    disabled={newPolicyForm.policyType === 'instant'}
                    style={newPolicyForm.policyType === 'instant' ? { background: '#e9ecef' } : {}}
                  />
                  {newPolicyForm.policyType === 'instant' && (
                    <small style={{ color: '#6c757d' }}>
                      Terms & Conditions are automatically set for instant tickets
                    </small>
                  )}
                </div>
                
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={newPolicyForm.isActive}
                      onChange={handleNewPolicyChange}
                    />
                    <span className="checkbox-text">Activate Policy Immediately</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleCancelCreate}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleCreatePolicy}>
                Create Policy
              </button>
            </div>
          </div>
        </div>
      )}


      {editingPolicy && !isFetchingDetails && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content1 edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Edit Booking Policy</h3>
              <button className="modal-close-btn" onClick={handleCancelEdit}>×</button>
            </div>
            <div className="modal-body">
              <div className="edit-form">
                {editForm.isInstant && (
                  <div style={{ 
                    background: '#fff3cd', 
                    border: '1px solid #ffc107', 
                    borderRadius: '4px', 
                    padding: '10px',
                    marginBottom: '15px',
                    color: '#856404'
                  }}>
                    ⚠️ This is an Instant Booking Policy - Cancellation and Refund are disabled
                  </div>
                )}
                
                <div className="form-group">
                  <label>Policy Name:</label>
                  <input
                    type="text"
                    name="policyName"
                    value={editForm.policyName || ''}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label>Cancellation Hours:</label>
                  <input
                    type="number"
                    name="cancellationHours"
                    value={editForm.cancellationHours || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    min="0"
                    disabled={editForm.isInstant}
                  />
                  {editForm.isInstant && (
                    <small style={{ color: '#dc3545' }}>Cancellation not allowed for instant tickets</small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Child Age Limit:</label>
                  <input
                    type="number"
                    name="childAgeLimit"
                    value={editForm.childAgeLimit || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    min="0"
                  />
                </div>
                
                <div className="form-group">
                  <label>Refund Percentage:</label>
                  <input
                    type="number"
                    name="refundPercentage"
                    value={editForm.refundPercentage || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    min="0"
                    max="100"
                    disabled={editForm.isInstant}
                  />
                  {editForm.isInstant && (
                    <small style={{ color: '#dc3545' }}>No refund for instant tickets</small>
                  )}
                </div>
                
                <div className="form-group">
                  <label>Description:</label>
                  <textarea
                    name="description"
                    value={editForm.description || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    disabled={editForm.isInstant}
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={editForm.isActive || false}
                      onChange={handleInputChange}
                    />
                    <span className="checkbox-text">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="action-btn cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="action-btn save" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {isFetchingDetails && <SkeletonModal />}
    </div>
  );
};

export default BookingPolicyManagement;